import { message } from 'antd';
import JSZip from 'jszip';
import { createScene, updateScene } from '../api/scenes';
import { createModel, uploadModel, updateModel } from '../api/models';
import { createPage, updatePageComponents } from '../api/pages';
import { uploadDashboardAsset } from '../api/assets';
import { downloadBackupFile, parseBackupError } from '../api/backup';
import { usePopupStore } from '../stores/popupStore';

// ============================================
// 备份导入导出（2026-10-06 起导出改为服务端权威执行）
// - 导出：JSON 元数据 / ZIP 打包（含资源文件）由 dtwin-server /backup/*/file 生成并强校验权限：
//   ZIP 默认仅超级管理员；JSON 按 tier（free/paid/superadmin）允许 + 每月额度（推广后台可配）
// - 免费用户点击 JSON 导出 → 服务端 403 EXPORT_JSON_FORBIDDEN → 前端弹付费推广页
// - 导入：仍在前端解析 .json / .zip（自动上传资源并重建，跨环境可用）
// ============================================

export type BackupMode = 'json' | 'zip';

/** 服务端导出错误（携带 code：EXPORT_JSON_FORBIDDEN / EXPORT_ZIP_FORBIDDEN / EXPORT_QUOTA_EXCEEDED） */
export class BackupHttpError extends Error {
  code?: string;
  status?: number;
  constructor(msg: string, code?: string, status?: number) {
    super(msg);
    this.code = code;
    this.status = status;
  }
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/** 调用服务端导出接口（统一把 axios/blob 错误转成带 code 的 BackupHttpError） */
const serverExport = async (kind: 'scenes' | 'pages', mode: BackupMode, id?: string) => {
  try {
    return await downloadBackupFile(kind, mode, id);
  } catch (e) {
    const info = await parseBackupError(e);
    throw new BackupHttpError(info.message || '导出失败', info.code, info.status);
  }
};

// ---------- 通用资源收集/还原（递归遍历任意 JSON 对象中的 /uploads/ 资源） ----------

type AsyncReplacer = (v: string) => Promise<string>;

const walkReplaceAsync = async (obj: unknown, replacer: AsyncReplacer, depth = 0): Promise<unknown> => {
  if (obj == null || depth > 12) return obj;
  if (typeof obj === 'string') return replacer(obj);
  if (Array.isArray(obj)) {
    const out: unknown[] = [];
    for (const item of obj) out.push(await walkReplaceAsync(item, replacer, depth + 1));
    return out;
  }
  if (typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj as Record<string, unknown>)) {
      out[k] = await walkReplaceAsync((obj as Record<string, unknown>)[k], replacer, depth + 1);
    }
    return out;
  }
  return obj;
};

const isUploadsUrl = (v: string) => v.startsWith('/uploads/');

/** zip 导入：把对象中 ./assets/ 相对资源解压上传到当前环境，替换为新 url；assetNames 提供原始素材名（zip 相对路径→原始名） */
async function restoreUploads(obj: unknown, zip: JSZip | null, assetNames?: Record<string, string>): Promise<unknown> {
  if (!zip) return obj;
  return walkReplaceAsync(obj, async (v) => {
    if (!v.startsWith('./assets/')) return v;
    const rel = v.replace(/^\.\//, '');
    const entry = zip.file(rel);
    if (!entry) return v;
    try {
      const blob = await entry.async('blob');
      // 优先用原始素材名（导入后素材库显示原始中文名，而非 asset-xxx.png）
      const fname = assetNames?.[rel] || rel.split('/').pop() || 'asset.bin';
      const up = await uploadDashboardAsset(new File([blob], fname));
      if (up?.url) return up.url;
    } catch {
      /* 上传失败保留相对路径（提示见后） */
    }
    return v;
  });
}

/** 拉取当前用户素材（url→原始名），供导出时记录原始素材名 */
// ============ 场景 ============

/** 导出场景备份（服务端权威执行；zip 仅超管，json 按套餐与月度额度） */
export async function exportScenesBackup(mode: BackupMode, sceneId?: string): Promise<number> {
  const { blob, filename, itemCount } = await serverExport('scenes', mode, sceneId);
  downloadBlob(blob, filename);
  return itemCount;
}

export async function importScenesBackup(file: File): Promise<number> {
  let manifest: any;
  let zip: JSZip | null = null;
  if (file.name.toLowerCase().endsWith('.zip')) {
    zip = await JSZip.loadAsync(await file.arrayBuffer());
    const entry = zip.file('manifest.json');
    if (!entry) throw new Error('zip 内缺少 manifest.json');
    manifest = JSON.parse(await entry.async('string'));
  } else {
    manifest = JSON.parse(await file.text());
  }
  if (manifest.type !== 'dtwin-scenes' || !Array.isArray(manifest.items)) {
    throw new Error('不是有效的场景备份文件');
  }
  const assetNames = manifest.assetNames || undefined;
  let ok = 0;
  for (const item of manifest.items) {
    const srcScene = (await restoreUploads(item.scene || {}, zip, assetNames)) as any;
    const created = await createScene({ name: srcScene.name || '导入场景', description: srcScene.description });
    const { id: _sid, createdAt: _sc, updatedAt: _su, thumbnail: _th, name: _n, description: _d, ...sceneRest } = srcScene;
    if (Object.keys(sceneRest).length > 0) await updateScene(created.id, sceneRest).catch(() => {});
    for (const m of item.models || []) {
      const { id: _mid, sceneId: _ms, url: srcUrl, ...modelData } = m;
      const isPreset = m.format === 'preset' || !!m.presetId;
      const isExternal = !isPreset && srcUrl && (srcUrl.startsWith('http') || srcUrl.startsWith('blob:') || srcUrl.startsWith('/uploads/'));
      if (!isPreset && !isExternal && zip && typeof srcUrl === 'string') {
        // zip 内相对路径 → 提取文件上传到新环境
        const rel = srcUrl.replace(/^\.\//, '');
        const entry = zip.file(rel);
        if (entry) {
          const blob = await entry.async('blob');
          const fname = rel.split('/').pop() || 'model.bin';
          try {
            const up = await uploadModel(created.id, new File([blob], fname), undefined);
            if (up?.id) {
              await updateModel(up.id, modelData).catch(() => {});
              continue;
            }
          } catch {
            /* 上传失败则退回无文件创建 */
          }
        }
      }
      await createModel({
        ...modelData,
        sceneId: created.id,
        url: isPreset ? srcUrl : srcUrl || '',
        ...(isPreset && m.presetId ? { presetId: m.presetId } : {}),
      }).catch(() => {});
    }
    ok++;
  }
  return ok;
}

// ============ 大屏 ============

/** 导出大屏备份（服务端权威执行；zip 仅超管，json 按套餐与月度额度） */
export async function exportPagesBackup(mode: BackupMode, pageId?: string): Promise<number> {
  const { blob, filename, itemCount } = await serverExport('pages', mode, pageId);
  downloadBlob(blob, filename);
  return itemCount;
}

export async function importPagesBackup(file: File): Promise<number> {
  let manifest: any;
  let zip: JSZip | null = null;
  if (file.name.toLowerCase().endsWith('.zip')) {
    zip = await JSZip.loadAsync(await file.arrayBuffer());
    const entry = zip.file('manifest.json');
    if (!entry) throw new Error('zip 内缺少 manifest.json');
    manifest = JSON.parse(await entry.async('string'));
  } else {
    manifest = JSON.parse(await file.text());
  }
  if (manifest.type !== 'dtwin-pages' || !Array.isArray(manifest.items)) {
    throw new Error('不是有效的大屏备份文件');
  }
  const assetNames = manifest.assetNames || undefined;
  let ok = 0;
  for (const p of manifest.items) {
    if (!p) continue;
    const page = (await restoreUploads(p, zip, assetNames)) as any;
    const created = await createPage({
      name: page.name || '导入大屏',
      description: page.description,
      sceneId: page.sceneId,
      config: page.config,
    });
    await updatePageComponents(created.id, page.components || []).catch(() => {});
    ok++;
  }
  return ok;
}

export const backupErrorHandler = (e: unknown) => {
  message.error(e instanceof Error ? `导入失败：${e.message}` : '导入失败');
};

/** 导出失败统一处理：免费用户 → 付费推广页；额度用尽/无权限 → 明确提示 */
export function backupExportErrorHandler(e: unknown): void {
  if (e instanceof BackupHttpError && e.code === 'EXPORT_JSON_FORBIDDEN') {
    usePopupStore.getState().openUpgrade();
    message.info('导出备份为付费功能，升级套餐后即可使用');
    return;
  }
  if (e instanceof BackupHttpError && e.code === 'EXPORT_ZIP_FORBIDDEN') {
    message.warning(e.message || 'ZIP 打包备份仅超级管理员可用');
    return;
  }
  if (e instanceof BackupHttpError && e.code === 'EXPORT_QUOTA_EXCEEDED') {
    message.warning(e.message || '本月导出额度已用完');
    return;
  }
  message.error(e instanceof Error ? `导出失败：${e.message}` : '导出失败');
}